import { Modal } from '../../components/Modal';
import { TypeAhead, type TypeAheadItem } from '../../components/TypeAhead';
import { useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useCompanyStore } from '../../stores/company';
import { useUiStore } from '../../stores/ui';
import { authApi } from '../../lib/api';
import { useSessionStore } from '../../stores/session';
import { usePermission } from '../../lib/permissions';

/**
 * Alt+F3 — Tally's binding, kept identical so muscle memory transfers.
 * Switching companies changes the `X-Company-Id` sent on every subsequent
 * request and refreshes the session-scoped permissions/restrictions so every
 * screen reflects the selected company immediately.
 */
export function CompanySwitcher() {
  const open = useUiStore((s) => s.companySwitcherOpen);
  const setOpen = useUiStore((s) => s.setCompanySwitcherOpen);
  const companies = useCompanyStore((s) => s.companies);
  const activeCompanyId = useCompanyStore((s) => s.activeCompanyId);
  const setActiveCompanyId = useCompanyStore((s) => s.setActiveCompanyId);
  const pushToast = useUiStore((s) => s.pushToast);
  const setSession = useSessionStore((s) => s.setSession);
  const setCompanies = useCompanyStore((s) => s.setCompanies);
  const can = usePermission();
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [switching, setSwitching] = useState(false);

  const items: TypeAheadItem[] = companies.map((c) => ({
    key: c.id,
    label: c.name,
    sublabel: c.id === activeCompanyId ? 'current' : undefined,
  }));

  async function handleSelect(item: TypeAheadItem) {
    const previous = activeCompanyId;
    setSwitching(true);
    setActiveCompanyId(item.key);
    try {
      const session = await authApi.session();
      setSession(session);
      setCompanies(session.companies);
      setActiveCompanyId(session.activeCompanyId ?? item.key);
      setOpen(false);
      // A detail route can retain a UUID that belongs to the previous company.
      // Reset every company-scoped workspace to a safe derived entry point.
      if (location.pathname.startsWith('/vouchers/') || location.pathname.startsWith('/reports') || location.pathname.startsWith('/onboarding') || location.pathname.startsWith('/coa') || location.pathname.startsWith('/company/')) {
        navigate('/onboarding', { replace: true });
      }
      pushToast({ kind: 'info', message: `Switched to ${item.label}.` });
    } catch (error) {
      setActiveCompanyId(previous);
      pushToast({ kind: 'error', message: error instanceof Error ? error.message : 'Unable to switch company.' });
    } finally {
      setSwitching(false);
    }
  }

  function createCompany() {
    const name = query.trim();
    setQuery('');
    setOpen(false);
    navigate(name ? `/companies/new?name=${encodeURIComponent(name)}` : '/companies/new');
  }

  const hasSubstringMatch = query.trim() === '' || items.some((item) => item.label.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <Modal open={open} onClose={() => { setQuery(''); setOpen(false); }} title="Switch company" widthClassName="max-w-md">
      <div className="mb-3 flex items-start justify-between gap-3 border border-[var(--border)] bg-[var(--bg-inset)] px-2.5 py-2">
        <div><p className="text-[12px] font-medium">Choose a set of books</p><p className="mt-0.5 text-[11px] text-[var(--text-faint)]">Type to filter, ↑↓ to move, Enter to switch.</p></div>
        {can('company:company:create') && <button className="button-primary shrink-0" onClick={createCompany}>Create company</button>}
      </div>
      <TypeAhead items={items} onSelect={handleSelect} onQueryChange={setQuery} disabled={switching} placeholder="Company name…" autoFocus emptyMessage="No companies match." />
      {!hasSubstringMatch && query.trim() && (
        <div className="mt-2 flex items-center justify-between gap-3 border border-[var(--accent)] bg-[var(--bg-selected)]/40 px-2.5 py-2">
          <p className="text-[11.5px] text-[var(--text-dim)]">No companies match — create <span className="font-medium text-[var(--text)]">{query.trim()}</span>.</p>
          {can('company:company:create') ? <button className="button-muted shrink-0" onClick={createCompany}>Create “{query.trim()}”</button> : <span className="text-[11px] text-[var(--text-faint)]">Ask an administrator</span>}
        </div>
      )}
      {!can('company:company:create') && <p className="mt-3 text-[11px] text-[var(--text-faint)]">Creating a company requires company:company:create permission.</p>}
    </Modal>
  );
}
